import { FC } from "react";
import { dollars } from "./dollars";
import { ChevronUpIcon } from "@heroicons/react/24/outline";
import type { SecondaryButton } from "./BookingOverview";
import { MAIN_HOVER, NO_TAP_SELECT, fadeUnlessBusy, useClearSelectionWhen } from "./noTapSelect";

const Spinner = <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />;

/**
 * The bar at the bottom of the phone booking (1NOW-11155): the total on the
 * left (tap it for the price details), the main button on the right. On the
 * car page it is fixed to the screen; inside the booking sheet it sits at the
 * bottom of the sheet's own column.
 */
const TotalBar: FC<{
  total: number | null;
  /** Left out where there is no price sheet to open (the Reserved page). */
  onPriceDetails?: () => void;
  /** Replaces "Price details" under the total (the Reserved page: "due by Oct 9"). */
  note?: string;
  label: string;
  /** For Meta only (see BookingOverview). */
  labelTo?: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
  /** Fixed to the screen (car page) rather than part of a column. */
  fixed?: boolean;
  /** After the amount, e.g. "/day" before dates are picked. */
  unit?: string;
  /** A second button (the review's "Pay later"): the two share a row under the total. */
  secondary?: SecondaryButton;
}> = ({ total, onPriceDetails, note, label, labelTo, onClick, disabled, busy, fixed, unit, secondary }) => {
  useClearSelectionWhen(busy || secondary?.busy);
  const main = (
    // key: a new label is a new button, so Meta's Event Setup Tool sees each step's button as its own (see BookingOverview).
    <button
      key={`${label} ${labelTo}`}
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      aria-label={label}
      className={`inline-flex min-h-[44px] items-center justify-center rounded-full bg-button-color [.design-a_&]:bg-primary-6000 [.design-a_&]:text-on-primary py-3 text-sm font-extrabold text-on-button transition-opacity ${MAIN_HOVER} ${fadeUnlessBusy(busy)} ${
        secondary ? "flex-1 px-4" : "min-w-[132px] px-6"
      }`}
    >
      {busy ? Spinner : <>{label}{labelTo && <span className="sr-only"> to {labelTo}</span>}</>}
    </button>
  );
  return (
    <div
      className={`${fixed ? "fixed inset-x-0 bottom-0 z-40" : "shrink-0"} border-t border-secondary-100 bg-bg-color text-text-color ${NO_TAP_SELECT}`}
      data-testid="total-bar"
    >
      <div className={`flex items-center gap-3 px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 ${secondary ? "flex-wrap" : ""}`}>
        {onPriceDetails ? (
          <button type="button" onClick={onPriceDetails} className="min-w-0 text-left" aria-label="Price details">
            <span className="block text-xl font-extrabold leading-tight tabular-nums">
              {total === null ? "—" : dollars(total)}
            </span>
            <span className="flex items-center gap-0.5 text-[11px] font-bold underline">
              {note ?? "Price details"}
              {!note && <ChevronUpIcon className="h-3 w-3" aria-hidden="true" />}
            </span>
          </button>
        ) : (
          <div className="min-w-0">
            <span className="block text-xl font-extrabold leading-tight tabular-nums">
              {total === null ? "—" : dollars(total)}
              {unit && <span className="text-sm font-bold">{unit}</span>}
            </span>
            {note && <span className="block text-[11px] font-bold">{note}</span>}
          </div>
        )}
        <span className="flex-1" />
        {secondary ? (
          <div className="flex basis-full gap-2">
            <button
              type="button"
              onClick={secondary.onClick}
              disabled={secondary.disabled || secondary.busy}
              className={`inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border-2 border-text-color/15 px-4 py-3 text-sm font-extrabold text-text-color transition-opacity ${fadeUnlessBusy(secondary.busy)}`}
            >
              {secondary.busy ? Spinner : secondary.label}
            </button>
            {main}
          </div>
        ) : (
          main
        )}
      </div>
    </div>
  );
};

export default TotalBar;
